import React, { useState, useEffect } from 'react'
import { Box, Button, Card, CardContent, Grid, Typography, Chip, IconButton, Tooltip } from '@mui/material'
import { useForm } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { format, subDays } from 'date-fns'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'
import FormProvider from '../../../common/hook-form/FormProvider'
import RHFDateRangePickerField from '../../../common/hook-form/RHFDateRangePickerField'
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined'
import { MerchantFilterCached } from '../../../common/ProshipFormComponents'
import ProzoDataGrid from '../../../common/ProzoDataGrid'
import { apiConstants, post } from '../../../common/constants'

const PAGE_SIZE = 20

function formatDateTime(isoString) {
  if (!isoString) return '—'
  try {
    return format(new Date(isoString), 'dd MMM yyyy, HH:mm')
  } catch {
    return isoString
  }
}

function formatDateOnly(isoString) {
  if (!isoString) return '—'
  try {
    return format(new Date(isoString), 'dd MMM yyyy')
  } catch {
    return isoString
  }
}

export default function MerchantReportsTab() {
  const { enqueueSnackbar } = useSnackbar()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(PAGE_SIZE)
  const [filters, setFilters] = useState(null)

  const methods = useForm({
    defaultValues: {
      merchant: null,
      fromDate: subDays(new Date(), 7),
      toDate: new Date(),
    },
  })

  const { handleSubmit, reset } = methods

  const handleReset = () => {
    reset({ merchant: null, fromDate: subDays(new Date(), 7), toDate: new Date() })
    setRows([])
    setSubmitted(false)
    setPage(0)
    setFilters(null)
  }

  const onSubmit = (data) => {
    setPage(0)
    setFilters({
      merchantId: data.merchant || undefined,
      fromDate: data.fromDate ? format(new Date(data.fromDate), 'dd-MM-yyyy') : undefined,
      toDate: data.toDate ? format(new Date(data.toDate), 'dd-MM-yyyy') : undefined,
    })
  }

  useEffect(() => {
    if (!filters) return
    setLoading(true)
    post(apiConstants.AUDIT_MERCHANT_REPORTS, { ...filters, offset: page * sizePerPage, limit: sizePerPage })
      .then((res) => {
        const raw = res?.data
        const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : []
        setRows(list.map((r, i) => ({
          ...r,
          id: i,
          signedLink: r.s3Link?.signedLink || null,
          signedURLExpiry: r.s3Link?.signedURLExpiry || null,
          signedURLExpired: r.s3Link?.signedURLExpired ?? false,
        })))
        setSubmitted(true)
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || 'Failed to fetch merchant reports activity'
        enqueueSnackbar(msg, { variant: 'error' })
      })
      .finally(() => setLoading(false))
  }, [filters, page, sizePerPage]) // eslint-disable-line react-hooks/exhaustive-deps

  const columns = [
    {
      field: 'createdAt',
      headerName: 'Generated At',
      flex: 1.2,
      renderCell: (p) => (
        <Typography variant="body2">{formatDateTime(p.value)}</Typography>
      ),
    },
    {
      field: 'merchantName',
      headerName: 'Merchant',
      flex: 1.2,
      renderCell: (p) => <Typography variant="body2">{p.value || '—'}</Typography>,
    },
    {
      field: 'reportFullName',
      headerName: 'Report',
      flex: 1.5,
      renderCell: (p) => (
        <Chip label={p.value || '—'} size="small" color="primary" variant="outlined" />
      ),
    },
    {
      field: 'fromDate',
      headerName: 'Report Period',
      flex: 1.5,
      renderCell: (p) => {
        const toDate = p.row?.toDate
        return (
          <Typography variant="body2">
            {formatDateOnly(p.value)}
            {toDate ? ` → ${formatDateOnly(toDate)}` : ''}
          </Typography>
        )
      },
    },
    {
      field: 'createdBy',
      headerName: 'Generated By',
      flex: 1.5,
      renderCell: (p) => <Typography variant="body2">{p.value || '—'}</Typography>,
    },
    {
      field: 'signedLink',
      headerName: 'Download',
      flex: 0.7,
      sortable: false,
      renderCell: (p) => {
        const { signedLink, signedURLExpiry, signedURLExpired } = p.row
        if (!signedLink) return <Typography variant="body2">—</Typography>
        if (signedURLExpired) {
          return (
            <Tooltip title="Link expired">
              <span>
                <IconButton size="small" disabled>
                  <LinkOffOutlinedIcon fontSize="small" color="error" />
                </IconButton>
              </span>
            </Tooltip>
          )
        }
        return (
          <Tooltip title={`Expires ${formatDateTime(signedURLExpiry)}`}>
            <IconButton
              size="small"
              component="a"
              href={signedLink}
              target="_blank"
              rel="noopener noreferrer"
              color="primary"
            >
              <FileDownloadOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      },
    },
  ]

  return (
    <Box>
      {/* Filter Card */}
      <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: 'none' }}>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fafafa', borderBottom: '1px solid', borderColor: 'divider' }}>
          <FilterAltOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'text.primary' }}>Search Filters</Typography>
        </Box>
        <CardContent>
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <MerchantFilterCached fullWidth />
              </Grid>
              <Grid item xs={12} md={3}>
                <RHFDateRangePickerField label="Date Range" />
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button type="submit" variant="contained" size="medium" disabled={loading} startIcon={<AssessmentOutlinedIcon />}>
                    Search
                  </Button>
                  <Button type="button" variant="outlined" size="medium" onClick={handleReset} startIcon={<RestartAltOutlinedIcon />}>
                    Reset
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </FormProvider>
        </CardContent>
      </Card>

      {/* Results Card */}
      {submitted && (
        <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: 'none' }}>
          <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#fafafa' }}>
            <Typography variant="subtitle2" fontWeight={600}>Results</Typography>
            <Chip label={`${rows.length} records`} size="small" color="primary" variant="outlined" />
          </Box>
          {rows.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <InboxOutlinedIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5 }} />
              <Typography variant="subtitle1" color="text.secondary" fontWeight={500}>No records found</Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>Try adjusting your filters and search again</Typography>
            </Box>
          ) : (
            <CardContent sx={{ px: 0, pb: '0 !important' }}>
              <ProzoDataGrid
                rows={rows}
                columns={columns}
                loading={loading}
                rowCount={Number.MAX_VALUE}
                page={page}
                setPage={setPage}
                sizePerPage={sizePerPage}
                setSizePerPage={setSizePerPage}
                paginationMode="server"
                disableColumnMenu
                disableColumnFilter
              />
            </CardContent>
          )}
        </Card>
      )}
    </Box>
  )
}
